import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type {
  ConfigureRemoteSpeakerRequestDto,
  RemoteSpeakerDayDto,
} from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import { deriveSuggestedRemoteSpeakerDayIds } from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useConfigureRemoteSpeakerMutation } from '@/state/mutations/contributors/use-configure-remote-speaker';
import { useNotifications } from '@/hooks/use-notifications';
import { getOriginEventId } from '@/helpers/event.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { useInsufficientRemoteSpeakerDaysInfo } from '@/hooks/use-insufficient-remote-speaker-days-info';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import { useProductsStore } from '@/state/stores/use-products-store';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders';
import { useCreateEventOrderMutation } from '@/state/mutations/orders/use-create-order';
import { useUpdateOrderMutation } from '@/state/mutations/orders/use-update-order';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto';
import { upsertOrderLineItemInPayload } from '@/helpers/order-line-item.helper';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal';
import { clampRemoteSpeakerDayCheckoutQuantity } from '@/utils/remote-speaker-status.utils';

export type RemoteSpeakerConfigureModalProps = {
  open: boolean;
  onClose: () => void;
  contributor: ContributorDto;
  enabled: boolean;
  scope: ConfigureRemoteSpeakerRequestDto['scope'];
  flowId?: string;
  sessionId?: string;
  roleId?: string;
  slotId?: string;
  flowContext?: FlowDto;
  /** When true, day checkboxes reflect the scope and cannot be changed. */
  daysReadOnly?: boolean;
};

export function RemoteSpeakerConfigureModal({
  open,
  onClose,
  contributor,
  enabled,
  scope,
  flowId,
  sessionId,
  roleId,
  slotId,
  flowContext,
  daysReadOnly = false,
}: RemoteSpeakerConfigureModalProps) {
  const { t } = useTranslation();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const originEventId = event ? getOriginEventId(event) : undefined;
  const { data: permissions } = usePermissionsQuery();
  const isEventManager =
    permissions?.events?.[originEventId ?? '']?.includes(EVENT_PERMISSIONS.MANAGE_EVENT_ROLES) ?? false;

  const suggestedDayIds = useMemo(
    () => deriveSuggestedRemoteSpeakerDayIds(contributor, eventPlan ?? event, flowContext),
    [contributor, eventPlan, event, flowContext],
  );

  const [selectedDayIds, setSelectedDayIds] = useState<string[]>(suggestedDayIds);
  const [defaultForNewRoles, setDefaultForNewRoles] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedDayIds(suggestedDayIds);
      setDefaultForNewRoles(false);
    }
  }, [open, suggestedDayIds]);

  const { mutateAsync: configureRemoteSpeaker, isPending } = useConfigureRemoteSpeakerMutation();
  const insufficientInfo = useInsufficientRemoteSpeakerDaysInfo(event, contributor, selectedDayIds);
  const catalogProducts = useProductsStore((s) => s.products);
  const { data: orders } = useGetEventOrdersQuery(originEventId);
  const { mutateAsync: createEventOrder } = useCreateEventOrderMutation();
  const { mutateAsync: updateOrder } = useUpdateOrderMutation();

  const displayName = contributor.profiles[0]?.displayName ?? t('event.detail.contributors.unknownContributor');

  const handleConfirm = async () => {
    if (!originEventId) return;

    try {
      const result = await configureRemoteSpeaker({
        eventId: originEventId,
        contributorId: contributor.id,
        scope,
        enabled,
        flowId,
        sessionId,
        roleId,
        slotId,
        applyToExistingContributions: enabled && isEventManager && scope === 'event' ? true : undefined,
        defaultForNewRoles: isEventManager ? defaultForNewRoles : undefined,
        dayIds: enabled ? selectedDayIds : [],
        purchaseIfNeeded: isEventManager,
      });

      if (result.checkoutRequired && insufficientInfo && isEventManager) {
        const product = catalogProducts?.find((entry) => entry.key === PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY);
        if (product) {
          const latestOrder = orders?.find(
            (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
          );
          const linePayload = {
            productId: product.id,
            quantity: clampRemoteSpeakerDayCheckoutQuantity(
              insufficientInfo.neededCredits,
              dayOptions.length,
              contributor,
            ),
            contributorId: contributor.id,
          };
          if (linePayload.quantity < 1) {
            notification.notifyError(t('event.detail.contributors.remoteSpeaker.noRemainingDaysToPurchase'));
          } else if (latestOrder?.status === ORDER_STATUS.DRAFT) {
            await updateOrder({
              orderId: latestOrder.id,
              lineItems: upsertOrderLineItemInPayload(latestOrder.lineItems, linePayload),
            });
            setCheckoutOpen(true);
          } else {
            await createEventOrder({ eventId: originEventId, lineItems: [linePayload] });
            setCheckoutOpen(true);
          }
        }
      }

      if (enabled) {
        const hasPending = result.remoteSpeakerDays.some((entry: RemoteSpeakerDayDto) => entry.status === 'pending');
        if (hasPending) {
          notification.notifySuccess(
            t('event.detail.contributors.remoteSpeaker.pendingNotification', { name: displayName }),
          );
        } else {
          notification.notifySuccess(t('session.remoteSpeaker.enabledNotification', { name: displayName }));
        }
      } else {
        notification.notifySuccess(t('session.remoteSpeaker.disabledNotification', { name: displayName }));
      }

      onClose();
    } catch {
      notification.notifyError(t('session.remoteSpeaker.toggleError', { name: displayName }));
    }
  };

  const dayOptions = eventPlan?.days ?? event?.days ?? [];
  const hasMultipleDays = dayOptions.length > 1;

  return (
    <>
      <Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>
          {enabled
            ? t('event.detail.contributors.remoteSpeaker.enableTitle', { name: displayName })
            : t('event.detail.contributors.remoteSpeaker.disableTitle', { name: displayName })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {enabled
              ? t('event.detail.contributors.remoteSpeaker.enableDescription', { name: displayName })
              : t('event.detail.contributors.remoteSpeaker.disableDescription', { name: displayName })}
          </DialogContentText>
          {enabled && hasMultipleDays ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">{t('event.detail.contributors.remoteSpeaker.daysTitle')}</Typography>
              {dayOptions.map((day, index) => {
                const dayId = day.id;
                const checked = selectedDayIds.includes(dayId);
                if (daysReadOnly && !checked) return null;
                return (
                  <FormControlLabel
                    key={dayId}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={daysReadOnly}
                        onChange={
                          daysReadOnly
                            ? undefined
                            : (event) => {
                                setSelectedDayIds((current) =>
                                  event.target.checked ? [...current, dayId] : current.filter((id) => id !== dayId),
                                );
                              }
                        }
                      />
                    }
                    label={t('event.detail.contributors.remoteSpeaker.dayLabel', {
                      number: index + 1,
                      defaultValue: `Day ${index + 1}`,
                    })}
                  />
                );
              })}
            </Stack>
          ) : null}
          {isEventManager && enabled ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={defaultForNewRoles}
                  onChange={(event) => setDefaultForNewRoles(event.target.checked)}
                />
              }
              label={t('event.detail.contributors.remoteSpeaker.defaultForNew')}
              sx={{ alignItems: 'center', mx: 0, mt: enabled && hasMultipleDays ? 1 : 2 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={isPending}>
            {isPending ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <EventCatalogOrderCheckoutModal
        open={checkoutOpen}
        event={eventPlan ?? event ?? undefined}
        onModalClose={() => setCheckoutOpen(false)}
      />
    </>
  );
}
